// Imports
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Params, useNavigate, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components/macro';
import Editor from '../../../../../../shareables/foundation/front-end/components/forms/fields/editor';
import Select from '../../../../../../shareables/foundation/front-end/components/forms/fields/select';
import { useAppDispatch, useAppSelector } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import APIButton from '../../../../../../shareables/integrations/api/components/button';
import APIForm, { APIFormOptions } from '../../../../../../shareables/integrations/api/components/form';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import {
	UploadImageFiles,
	UploadImageParameters,
	UploadImageResponse,
} from '../../../../../../shareables/types/api/main/website/editor/image/upload';
import { GetTopicResponse } from '../../../../../../shareables/types/api/main/website/page/type/215/topic/get';
import { PostTopicParameters } from '../../../../../../shareables/types/api/main/website/page/type/215/topic/post';
import convertHTMLToSnippet from '../../../../../../shareables/utils/convert-html-to-snippet';
import parseAndSanitizeMarkdown from '../../../../../../shareables/utils/markdown/parse-and-sanitize';
import removePersistedFormEntry from '../../../../../../utils/remove-persisted-form-entry';
import useMemberCheck from '../../../../../../utils/use-member-check';
import { GeneratedThemeProps } from '../../../../../templates/template-style';
import SignInAnchor from '../../../../sign-in-anchor';
import Byline from '../shared-components/byline';
import { StyledEditorGeneratedContentContainer } from '../shared-components/styled-editor-generated-content-container';


// Styled components
const cardStyle = css`
	background: #fafafa;
	border: 0.0625rem solid #eee;
	border-radius: 0.1875rem;
	padding: 1rem;
	position: relative;
	overflow-wrap: break-word;
	
	img {
		max-width: 100%;
	}
`;

const timelineStyle = css`
	&::before {
		content: '';
		position: absolute;
		top: -1.6rem;
		left: calc(-2.5rem - 0.0625rem);
		border-left: 0.125rem solid #eee;
		height: calc(100% + 1.6rem);
	}
	
	&:last-child {
		&::before {
			height: calc(50% + 1.6rem);
		}
	}
`;

const TopicContainer = styled.div`
	overflow-wrap: break-word;
	
	&& {
		margin-bottom: 0;
	}
	
	button {
		margin-top: 0.375rem;
		margin-bottom: 0.625rem;
	}
	
	hr {
		margin-top: 0.625rem;
		border: 0;
		border-top: 0.0625rem solid #eee;
	}
`;

const Title = styled.h3<{ isReply?: boolean }>`
	display: inline-block;
	margin-bottom: 0.25rem;
	line-height: 1;
	font-weight: 400;
	font-size: 1.5rem;
	overflow-wrap: anywhere;
	
	${(props) =>
		props.isReply &&
		css`
			margin-bottom: 1rem;
		`}
`;

const Content = styled(StyledEditorGeneratedContentContainer)<{ isTopic?: boolean }>`
	width: 100%;
	margin-bottom: 0.25rem;
	
	p:last-child {
		margin-bottom: 0;
	}
	
	${(props) =>
		props.isTopic &&
		css`
			margin-top: 0.1563rem;
		`}
`;

const CommentsContainer = styled.div`
	padding-top: 1.5rem;
	padding-left: 5rem;
	position: relative;
`;

const Wrapper = styled.div<{ isDeleted?: boolean }>`
	${(props) =>
		props.isDeleted &&
		css`
			opacity: 0.4;
		`}
`;

const Comment = styled.div`
	${cardStyle}
	${timelineStyle}
	
	margin-bottom: 1.5rem;
`;

const Posted = styled.div`
	position: absolute;
	top: calc(50% - 1.5rem);
	left: -4rem;
	
	display: flex;
	align-items: center;
	text-align: center;
	justify-content: center;
	border: 0.0625rem solid #eee;
	border-radius: 50%;
	
	width: 3rem;
	min-width: 3rem;
	height: 3rem;
	min-height: 3rem;
	
	line-height: 1;
	
	padding: 0.5rem;
	
	background: #fafafa;
	z-index: 2;
	
	font-weight: 500;
`;

const NonUniqueDay = styled.div`
	position: absolute;
	top: calc(50% - 0.5rem);
	left: -3rem;
	width: 1rem;
	height: 1rem;
	background: #fafafa;
	border: 0.0625rem solid #eee;
	border-radius: 50%;
	z-index: 2;
`;

const Day = styled.span`
	font-size: larger;
`;

const Month = styled.span`
	font-size: smaller;
`;

const BottomPortion = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	row-gap: 1rem;
	column-gap: 0.375rem;
	
	* {
		margin-bottom: 0 !important;
	}
`;

const Divider = styled.hr`
	margin: 0.75rem 0;
	border: 0;
	border-top: 0.0625rem solid #eee;
`;

const DeleteButton = styled(APIButton)`
	margin-left: auto;
	
	svg.svg-inline--fa {
		margin-right: 0.25em;
	}
`;

const ReplyContainer = styled.div`
	position: relative;
`;

const Reply = styled.div<{ theme: GeneratedThemeProps }>`
	${cardStyle}
	${timelineStyle}
	
	form {
		margin-bottom: 0 !important;
	}
	
	a {
		cursor: pointer;
	}
	
	.ck.ck-editor__editable_inline {
		ul,
		ol {
			margin-left: 2em;
		}
	}
`;


// Define the accepted props
interface Props {
	pageID: number;
	pageTitle: string;
	storybookTopic?: GetTopicResponse;
}


// Function component
const Topic: React.FC<Props> = ({ pageID, pageTitle, storybookTopic }) => {
	// Use state
	const [topic, setTopic] = useState<GetTopicResponse | null>(storybookTopic ?? null);
	const [formKey, setFormKey] = useState(0);
	const [topicDeleted, setTopicDeleted] = useState(false);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const member = useAppSelector((state) => state.self.member);
	const signedIn = useAppSelector((state) => state.common.user.signedIn);
	
	
	// Use React Router functionality
	const { topicIdentifier } = useParams<Params>();
	const navigate = useNavigate();
	
	
	// Parse topic ID from params
	const topicID = topicIdentifier?.split('-').pop();
	
	
	// Determine if signed in member can reply
	const isAllowedToReply = !useMemberCheck({ type: 'ACCOUNT_ISSUE' });
	
	
	// Function that loads a topic
	const memoizedLoadTopic = useCallback(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetTopicResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/page/id_${pageID}/type/215/topic/${topicID ?? ''}`,
				completion: (json) => {
					setTopic(json.response);
				},
			})
		);
	}, [dispatch, pageID, topicID]);
	
	
	// Load topic on mount
	useEffect(() => {
		memoizedLoadTopic();
	}, [memoizedLoadTopic]);
	
	
	// Navigate away after topic deletion
	//  -> Delay the navigation to the next render cycle so `unsavedChangesWarning` is disabled
	useEffect(() => {
		if (!topicDeleted) {
			return;
		}
		
		navigate('../..', { relative: 'path' });
	}, [topicDeleted, navigate]);
	
	
	// If loading, then display nothing
	if (topic === null) {
		return null;
	}
	
	
	// Parse and sanitize topic content
	const sanitizedContent = {
		__html: parseAndSanitizeMarkdown(topic.text),
	};
	
	
	// Determine last comment date
	const lastComment = topic.comments[topic.comments.length - 1];
	const lastCommentDate = lastComment ? new Date(lastComment.posted) : new Date(Date.now());
	
	
	// Determine if today is on the same day
	const today = new Date(Date.now());
	const isSameDayAsLastComment = lastCommentDate.toDateString() === today.toDateString();
	
	
	// Build the reply date
	const replyDateJSX = isSameDayAsLastComment ? (
		<NonUniqueDay />
	) : (
		<Posted>
			<small className='is-gray' aria-label='Posted on'>
				<Day>{today.getDate()}</Day>
				<br />
				<Month>{today.toLocaleDateString(undefined, { month: 'short' }).toLocaleUpperCase()}</Month>
			</small>
		</Posted>
	);
	
	
	// Build form options
	const formOptions: APIFormOptions<PostTopicParameters, undefined, undefined> = {
		action: 'POST',
		uri: `/website/${window.location.hostname}/page/id_${pageID}/type/215/topic/${topicID ?? ''}/comment`,
		submitButton: {
			text: 'Comment',
		},
		unsavedChangesWarning: {
			disabled: topicDeleted,
		},
	};
	
	
	// Handle resetting form on completion
	const handleFormCompletion = () => {
		// Update key
		setFormKey((previousKey) => previousKey + 1);
		
		
		// Reset form
		removePersistedFormEntry(
			`POST /website/${window.location.hostname}/page/id_${pageID}/type/215/topic/${topicID ?? ''}/comment`
		);
		
		
		// Reload topic
		memoizedLoadTopic();
	};
	
	
	// Return JSX
	return (
		<React.Fragment>
			<Helmet title={`${topic.title} | ${pageTitle}`} />
			
			<TopicContainer>
				<Title>{topic.title}</Title>
				<Byline post={topic} created={topic.posted} action='posted' />
				
				<Content dangerouslySetInnerHTML={sanitizedContent} isTopic />
				
				{member && member.id === topic.submitter?.memberID && (
					<DeleteButton
						options={{
							action: 'DELETE',
							uri: `/website/${window.location.hostname}/page/id_${pageID}/type/215/topic/${topicID ?? ''}`,
							warning: {
								message: 'Deleting this topic is permanent.',
								button: `Yes, delete “${topic.title}”`,
							},
						}}
						onCompletion={() => setTopicDeleted(true)}
						color='danger'
						smallest
					>
						<FontAwesomeIcon icon={faTrash} />
						Delete my topic
					</DeleteButton>
				)}
				
				<hr />
			</TopicContainer>
			
			<CommentsContainer>
				{topic.comments.map((comment, i) => {
					// Build posted date
					let postedJSX: JSX.Element = <NonUniqueDay />;
					
					if (i === 0 || (i > 0 && comment.posted.slice(0, 10) !== topic.comments[i - 1].posted.slice(0, 10))) {
						const date = new Date(comment.posted);
						
						postedJSX = (
							<Posted>
								<small className='is-gray' aria-label='Posted on'>
									<Day>{date.getDate()}</Day>
									<br />
									<Month>{date.toLocaleDateString(undefined, { month: 'short' }).toLocaleUpperCase()}</Month>
								</small>
							</Posted>
						);
					}
					
					
					// Parse and sanitize comment content
					const sanitizedHTML = parseAndSanitizeMarkdown(comment.text ?? '');
					
					
					// Build comment deletion snippet
					const deletionSnippet = convertHTMLToSnippet(sanitizedHTML);
					
					
					// Return JSX
					return (
						<Comment key={comment.id}>
							{postedJSX}
							
							<Wrapper isDeleted={!comment.text}>
								{comment.text ? (
									<Content
										dangerouslySetInnerHTML={{
											__html: sanitizedHTML,
										}}
									/>
								) : (
									<em>Comment deleted</em>
								)}
								
								{comment.text && <Divider />}
								
								{comment.text && (
									<BottomPortion>
										<Byline post={comment} created={comment.posted} action='commented' moreSpace />
										
										{comment.text !== null && member && member.id === comment.submitter?.memberID && (
											<DeleteButton
												options={{
													action: 'DELETE',
													uri: `/website/${window.location.hostname}/page/id_${pageID}/type/215/topic/${topicID ?? ''}/comment/${comment.id}`,
													warning: {
														message: 'Deleting this comment is permanent.',
														button: `Yes, delete ${
															deletionSnippet
																? `“${deletionSnippet}”`
																: `comment posted on ${new Date(comment.posted).toLocaleDateString(undefined, {
																		month: 'long',
																		day: 'numeric',
																		year: 'numeric',
																		hour: 'numeric',
																		minute: '2-digit',
																	})}`
														}`,
													},
												}}
												onCompletion={memoizedLoadTopic}
												color='danger'
												smallest
											>
												<FontAwesomeIcon icon={faTrash} />
												Delete my comment
											</DeleteButton>
										)}
									</BottomPortion>
								)}
							</Wrapper>
						</Comment>
					);
				})}
				
				<ReplyContainer>
					<Reply>
						{replyDateJSX}
						<Title isReply>Reply to this conversation</Title>
						
						{signedIn ? (
							isAllowedToReply ? (
								<APIForm options={formOptions} onCompletion={handleFormCompletion} key={formKey}>
									<Editor
										name='text'
										label='Your comment'
										handleUpload={(file) =>
											new Promise((resolve, reject) => {
												void dispatch(
													apiActions.call<UploadImageParameters, UploadImageFiles, UploadImageResponse>({
														action: 'UPLOAD',
														uri: `/website/${window.location.hostname}/editor/image`,
														parameters: {
															pageID,
														},
														files: {
															image: file,
														},
														completion: (json) => {
															if (json.status === 'error') {
																reject(json.message);
																return;
															}
															
															resolve(json.response.url);
														},
														handleErrorsAutomatically: false,
													})
												);
											})
										}
										outputMarkdown
									/>
									
									<Select name='identifier' label='Your name' initialValue='MEMBER' required>
										<option value='MEMBER'>{member?.names.member}</option>
										{member?.names.member !== member?.names.account && (
											<option value='ACCOUNT'>{member?.names.account}</option>
										)}
										<option value='FAMILY'>{member?.names.family}</option>
									</Select>
								</APIForm>
							) : (
								<div>
									<em>
										A website administrator must {member?.account.suspended ? 'reinstate' : 'approve'} your account
										before you can reply to forum posts.
									</em>
								</div>
							)
						) : (
							<div>
								<em>
									Please <SignInAnchor>sign in</SignInAnchor> before posting a comment.
								</em>
							</div>
						)}
					</Reply>
				</ReplyContainer>
			</CommentsContainer>
		</React.Fragment>
	);
};

export default Topic;
